import {
  memo,
  useMemo,
  type KeyboardEvent,
  type MouseEvent,
  type ReactElement,
} from "react";
import clsx from "clsx";
import { Loader2 } from "lucide-react";

type NotificationViewLike<TDescriptor = unknown> = {
  id: string;
  category: string;
  message?: string | null;
  createdAt: string;
  requester?: {
    name?: string | null;
  } | null;
  view?: {
    title?: TDescriptor;
    message?: TDescriptor;
    priority?: number;
  } | null;
};

export interface NotificationItemProps<
  TDescriptor = unknown,
  TItem extends NotificationViewLike<TDescriptor> = NotificationViewLike<TDescriptor>,
> {
  item: TItem;
  isUpdating: boolean;
  timestampLabel: string;
  resolveCopy: (descriptor: TDescriptor | undefined, fallback?: string) => string;
  onNavigate: (item: TItem) => Promise<void> | void;
  onDelete: (item: TItem) => Promise<void> | void;
  dismissLabel: string;
  categoryLabels: {
    action: string;
    info: string;
  };
}

const NotificationItemComponent = <
  TDescriptor,
  TItem extends NotificationViewLike<TDescriptor>,
>({
  item,
  isUpdating,
  timestampLabel,
  resolveCopy,
  onNavigate,
  onDelete,
  dismissLabel,
  categoryLabels,
}: NotificationItemProps<TDescriptor, TItem>) => {
  const isAction = item.category === "action";
  const statusLabel = isAction ? categoryLabels.action : categoryLabels.info;
  const titleCopy = resolveCopy(item.view?.title, item.message ?? undefined);
  const messageCopy = resolveCopy(
    item.view?.message,
    item.requester?.name ?? "",
  );

  const handleAction = () => {
    if (isUpdating || !isAction) {
      return;
    }
    void onNavigate(item);
  };

  const handleDismiss = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (isUpdating || isAction) {
      return;
    }
    void onDelete(item);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleAction();
    }
  };

  const rightStatus = useMemo(() => {
    if (isUpdating) {
      return (
        <Loader2
          className="h-4 w-4 animate-spin text-content-accent"
          aria-hidden
        />
      );
    }
    return (
      <span
        className={clsx(
          "flex h-2.5 w-2.5 rounded-full",
          isAction
            ? "bg-primary shadow-[0_0_8px_rgb(var(--color-primary)_/_0.45)]"
            : "bg-surface-tertiary",
        )}
        role="img"
        aria-label={statusLabel}
      />
    );
  }, [isAction, isUpdating, statusLabel]);

  return (
    <div
      role={isAction ? "button" : "article"}
      tabIndex={isAction ? 0 : -1}
      onClick={isAction ? handleAction : undefined}
      onKeyDown={isAction ? handleKeyDown : undefined}
      className={clsx(
        "flex w-full flex-col rounded-lg border border-border-subtle bg-surface-primary p-4 shadow-sm transition-colors duration-200 ease-out focus:outline-none",
        isUpdating
          ? "cursor-progress opacity-60"
          : isAction
            ? "cursor-pointer hover:border-border-strong hover:bg-surface-secondary/60 focus-visible:ring-2 focus-visible:ring-primary/20"
            : "cursor-default",
      )}
      data-category={item.category}
    >
      <span className="text-[10px] leading-none text-content-muted">
        {timestampLabel}
      </span>

      <div className="mt-2 flex flex-col gap-1">
        <p className="text-[15px] font-bold leading-tight tracking-tight text-content-primary">
          {titleCopy}
        </p>
        {messageCopy ? (
          <p className="text-[13px] leading-snug text-content-muted">
            {messageCopy}
          </p>
        ) : null}
      </div>

      <div className="mt-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="flex h-4 items-center">{rightStatus}</div>
          <span className="sr-only">{statusLabel}</span>
        </div>

        {!isAction ? (
          <button
            type="button"
            onClick={handleDismiss}
            disabled={isUpdating}
            className="px-1 text-[12px] font-semibold leading-none text-content-accent transition-colors hover:text-primary active:opacity-70 focus:outline-none focus-visible:underline"
          >
            {dismissLabel}
          </button>
        ) : null}
      </div>
    </div>
  );
};

export const NotificationItem = memo(NotificationItemComponent) as <
  TDescriptor,
  TItem extends NotificationViewLike<TDescriptor>,
>(
  props: NotificationItemProps<TDescriptor, TItem>,
) => ReactElement;
export default NotificationItem;
